import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { ApiParams, ENDPOINTS, fetchData } from '../services/apiService';

interface DataContextProps {
  data: Record<ENDPOINTS, any>;
  fetchAndSetData: (type: ENDPOINTS, params: ApiParams) => Promise<void>;
  fetchMultipleAndSetData: (
    requests: { type: ENDPOINTS; params: ApiParams }[],
  ) => Promise<{ type: ENDPOINTS; data: any }[]>;
}

const DataContext = createContext<DataContextProps | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<Record<ENDPOINTS, any>>(
    {} as Record<ENDPOINTS, any>,
  );

  const isFetching = useRef<Record<string, boolean>>({});

  const fetchAndSetData = useCallback(
    async (type: ENDPOINTS, params: ApiParams) => {
      const key = `${type}_${JSON.stringify(params)}`;
      if (isFetching.current[key]) {
        return;
      }

      try {
        isFetching.current[key] = true;
        const fetchedData = await fetchData(type, params);
        setData((prevData) => ({
          ...prevData,
          [type]: fetchedData,
        }));
      } catch (error) {
        console.error(`Error fetching data for ${type}:`, error);
      } finally {
        isFetching.current[key] = false;
      }
    },
    [],
  );

  const fetchMultipleAndSetData = useCallback(
    async (requests: { type: ENDPOINTS; params: ApiParams }[]) => {
      const promises = requests.map(({ type, params }, index) => {
        const key = `${type}_${JSON.stringify(params)}`;
        if (isFetching.current[key]) {
          return Promise.resolve(null);
        }

        isFetching.current[key] = true;

        return fetchData(type, params)
          .then((fetchedData) => {
            return { type: type + '#' + index, data: fetchedData };
          })
          .catch((error) => {
            console.error(`Error fetching data for ${type}:`, error);
            return null;
          })
          .finally(() => {
            isFetching.current[key] = false;
          });
      });

      const results = await Promise.all(promises);

      return results.filter(
        (result): result is { type: ENDPOINTS; data: any } => result !== null,
      );
    },
    [],
  );

  const contextValue = React.useMemo(
    () => ({ data, fetchAndSetData, fetchMultipleAndSetData }),
    [data, fetchAndSetData, fetchMultipleAndSetData],
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};
